(() => {
  const COMPONENT_SELECTOR = "[data-complementary-products]";

  const moneyFormatter = (root) =>
    new Intl.NumberFormat(document.documentElement.lang || "fr-FR", {
      style: "currency",
      currency: root.dataset.currency || "EUR",
    });

  const availableVariants = (product) =>
    product.variants.filter((variant) => variant.available);

  const selectedVariant = (item) => {
    const select = item.querySelector("[data-complementary-variant]");
    return select?.selectedOptions[0] || null;
  };

  const selectedItems = (root) =>
    [...root.querySelectorAll("[data-complementary-item]")].filter(
      (item) => item.querySelector("[data-complementary-checkbox]")?.checked,
    );

  const updateSelection = (root) => {
    const items = selectedItems(root);
    const total = items.reduce(
      (sum, item) => sum + Number(selectedVariant(item)?.dataset.price || 0),
      0,
    );
    const totalElement = root.querySelector("[data-complementary-products-total]");
    const submit = root.querySelector("[data-complementary-products-submit]");
    if (totalElement) totalElement.textContent = moneyFormatter(root).format(total / 100);
    if (submit) submit.disabled = items.length === 0;
  };

  const productImageUrl = (source) => {
    if (!source) return "";
    const url = new URL(source, window.location.origin);
    url.searchParams.set("width", "240");
    return url.href;
  };

  const createProductItem = (product, root, index) => {
    const variants = availableVariants(product);
    if (!variants.length) return null;

    const item = document.createElement("li");
    item.className = "complementary-products__item";
    item.dataset.complementaryItem = "";

    const checkbox = document.createElement("input");
    checkbox.className = "complementary-products__checkbox";
    checkbox.type = "checkbox";
    checkbox.dataset.complementaryCheckbox = "";
    checkbox.id = `ComplementaryProduct-${product.id}-${index}`;
    checkbox.setAttribute("aria-label", `Sélectionner ${product.title}`);

    const media = document.createElement("a");
    media.className = "complementary-products__media";
    media.href = product.url;
    media.setAttribute("aria-label", product.title);
    if (product.featured_image) {
      const image = document.createElement("img");
      image.src = productImageUrl(product.featured_image);
      image.alt = product.title;
      image.loading = "lazy";
      image.width = 120;
      image.height = 120;
      media.append(image);
    } else {
      const placeholder = document.createElement("span");
      placeholder.className = "complementary-products__placeholder";
      placeholder.textContent = product.title.slice(0, 1).toUpperCase();
      placeholder.setAttribute("aria-hidden", "true");
      media.append(placeholder);
    }

    const content = document.createElement("div");
    content.className = "complementary-products__content";

    const title = document.createElement("a");
    title.className = "complementary-products__title";
    title.href = product.url;
    title.textContent = product.title;

    const price = document.createElement("span");
    price.className = "complementary-products__price";
    price.dataset.complementaryPrice = "";
    price.textContent = moneyFormatter(root).format(variants[0].price / 100);

    const variantLabel = document.createElement("label");
    variantLabel.className = "complementary-products__variant-label";
    variantLabel.htmlFor = `ComplementaryVariant-${product.id}-${index}`;
    variantLabel.textContent = variants.length > 1 ? "Choisir une option" : "Option";

    const select = document.createElement("select");
    select.className = "complementary-products__variant";
    select.id = variantLabel.htmlFor;
    select.dataset.complementaryVariant = "";
    variants.forEach((variant) => {
      const option = document.createElement("option");
      option.value = variant.id;
      option.dataset.price = variant.price;
      option.textContent = variant.title;
      select.append(option);
    });

    content.append(title, price);
    if (variants.length > 1 || variants[0].title !== "Default Title") {
      content.append(variantLabel, select);
    } else {
      select.className = "visually-hidden";
      select.setAttribute("aria-label", `Option pour ${product.title}`);
      content.append(select);
    }
    item.append(checkbox, media, content);
    return item;
  };

  const addSelectedProducts = async (root) => {
    const submit = root.querySelector("[data-complementary-products-submit]");
    const status = root.querySelector("[data-complementary-products-status]");
    const cartLink = root.querySelector("[data-complementary-products-cart-link]");
    const items = selectedItems(root).map((item) => ({
      id: Number(selectedVariant(item).value),
      quantity: 1,
    }));
    if (!items.length || !submit) return;

    submit.disabled = true;
    submit.setAttribute("aria-busy", "true");
    if (status) status.textContent = "Ajout en cours...";
    if (cartLink) cartLink.hidden = true;

    try {
      const response = await fetch(root.dataset.cartAddUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) throw new Error("Cart add failed");

      if (status) status.textContent = "Accessoires ajoutés au panier.";
      if (cartLink) cartLink.hidden = false;
      root.querySelectorAll("[data-complementary-checkbox]").forEach((checkbox) => {
        checkbox.checked = false;
      });
      updateSelection(root);
      document.dispatchEvent(new CustomEvent("cart:updated", {
        detail: { open: true, added: true },
      }));
    } catch {
      if (status) status.textContent = "Impossible d'ajouter la sélection. Réessayez.";
      submit.disabled = false;
    } finally {
      submit.removeAttribute("aria-busy");
    }
  };

  const loadComponent = async (root) => {
    if (root.dataset.complementaryReady === "true") return;
    root.dataset.complementaryReady = "true";

    try {
      const response = await fetch(root.dataset.recommendationsUrl, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return;
      const payload = await response.json();
      const products = payload.products || [];
      const list = root.querySelector("[data-complementary-products-list]");
      if (!list) return;

      products.forEach((product, index) => {
        const item = createProductItem(product, root, index);
        if (item) list.append(item);
      });
      if (!list.children.length) return;

      root.hidden = false;
      root.setAttribute("aria-busy", "false");
      updateSelection(root);
    } catch {
      root.hidden = true;
    }
  };

  const handleChange = (event) => {
    const root = event.target.closest(COMPONENT_SELECTOR);
    if (!root) return;
    if (event.target.matches("[data-complementary-variant]")) {
      const item = event.target.closest("[data-complementary-item]");
      const price = item?.querySelector("[data-complementary-price]");
      if (price) {
        price.textContent = moneyFormatter(root).format(
          Number(selectedVariant(item).dataset.price) / 100,
        );
      }
    }
    updateSelection(root);
  };

  const init = () => {
    document.querySelectorAll(COMPONENT_SELECTOR).forEach(loadComponent);
  };

  if (window.CrazyPrintComplementaryProducts) {
    window.CrazyPrintComplementaryProducts.init();
    return;
  }

  window.CrazyPrintComplementaryProducts = { init };
  document.addEventListener("change", handleChange);
  document.addEventListener("click", (event) => {
    const submit = event.target.closest("[data-complementary-products-submit]");
    if (submit) addSelectedProducts(submit.closest(COMPONENT_SELECTOR));
  });
  document.addEventListener("shopify:section:load", init);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
